// Import documentation content components - Getting Started
import InstallationContent from '../docs/getting-started/Installation';
import OpeningWorkspaceContent from '../docs/getting-started/OpeningWorkspace';
import NavigatingFileTreeContent from '../docs/getting-started/NavigatingFileTree';
import MultipleWorkspacesContent from '../docs/getting-started/MultipleWorkspaces';

// File Selection
import SelectionMethodsContent from '../docs/file-selection/SelectionMethods';
import SortingFilesContent from '../docs/file-selection/SortingFiles';
import FilteringFilesContent from '../docs/file-selection/FilteringFiles';
import SearchingFilesContent from '../docs/file-selection/SearchingFiles';
import ContextBuilderContent from '../docs/file-selection/ContextBuilder';

// Prompt Management
import StoringPromptsContent from '../docs/prompt-management/StoringPrompts';
import ComposingPromptsContent from '../docs/prompt-management/ComposingPrompts';
import CodeMapsContent from '../docs/prompt-management/CodeMaps';

// Workspace Management
import ManagingWorkspacesContent from '../docs/workspace-management/ManagingWorkspaces';
import FileOperationsContent from '../docs/workspace-management/FileOperations';
import FileWatchingContent from '../docs/workspace-management/FileWatching';
import URLSchemeContent from '../docs/workspace-management/URLScheme';

// API Integration
import SupportedModelsContent from '../docs/api-integration/SupportedModels';
import IntegratedChatContent from '../docs/api-integration/IntegratedChat';
import ProModeContent from '../docs/api-integration/ProMode';

// Applying Changes
import MultiFileEditsContent from '../docs/applying-changes/MultiFileEdits';
import ApplyModeContent from '../docs/applying-changes/ApplyMode';

// FAQ
import WindowsLinuxContent from '../docs/faq/WindowsLinux';
import APIKeysContent from '../docs/faq/APIKeys';
import SoloProjectContent from '../docs/faq/SoloProject';
import WhatIsRepoPromptContent from '../docs/faq/WhatIsRepoPrompt';
import CursorWindsurfDifferenceContent from '../docs/faq/CursorWindsurfDifference';
import PayForServicesContent from '../docs/faq/PayForServices';
import PromptCachingContent from '../docs/faq/PromptCaching';
import ManageSubscriptionContent from '../docs/faq/ManageSubscription';

// Optimized data structure with O(1) lookup instead of array iterations
const documentationData = {
  sections: {
    'getting-started': {
      id: 'getting-started',
      title: 'Getting Started',
      order: 1,
      subsections: {
        'installation': {
          id: 'installation',
          title: 'Installation',
          order: 1,
          component: InstallationContent
        },
        'opening-workspace': {
          id: 'opening-workspace',
          title: 'Opening a Workspace',
          order: 2,
          component: OpeningWorkspaceContent
        },
        'navigating-file-tree': {
          id: 'navigating-file-tree',
          title: 'Navigating the File Tree',
          order: 3,
          component: NavigatingFileTreeContent
        },
        'multiple-workspaces': {
          id: 'multiple-workspaces',
          title: 'Multiple Folders in a Workspace',
          order: 4,
          component: MultipleWorkspacesContent
        }
      }
    },
    'file-selection': {
      id: 'file-selection',
      title: 'File Selection',
      order: 2,
      subsections: {
        'selection-methods': {
          id: 'selection-methods',
          title: 'Selecting Files for Context',
          order: 1,
          component: SelectionMethodsContent
        },
        'sorting-files': {
          id: 'sorting-files',
          title: 'Sorting and Viewing Files',
          order: 2,
          component: SortingFilesContent
        },
        'filtering-files': {
          id: 'filtering-files',
          title: 'Filtering Files',
          order: 3,
          component: FilteringFilesContent
        },
        'searching-files': {
          id: 'searching-files',
          title: 'Searching Files',
          order: 4,
          component: SearchingFilesContent
        },
        'context-builder': {
          id: 'context-builder',
          title: 'AI Context Builder',
          order: 5,
          component: ContextBuilderContent
        }
      }
    },
    'workspace-management': {
      id: 'workspace-management',
      title: 'Workspace Management',
      order: 3,
      subsections: {
        'managing-workspaces': {
          id: 'managing-workspaces',
          title: 'Managing Workspaces',
          order: 1,
          component: ManagingWorkspacesContent
        },
        'file-operations': {
          id: 'file-operations',
          title: 'File Operations and Preview',
          order: 2,
          component: FileOperationsContent
        },
        'file-watching': {
          id: 'file-watching',
          title: 'Automatic File System Watching',
          order: 3,
          component: FileWatchingContent
        },
        'url-scheme': {
          id: 'url-scheme',
          title: 'URL Scheme Integration',
          order: 4,
          component: URLSchemeContent
        }
      }
    },
    'prompt-management': {
      id: 'prompt-management',
      title: 'Prompt Management',
      order: 4,
      subsections: {
        'storing-prompts': {
          id: 'storing-prompts',
          title: 'Using Stored Prompts',
          order: 1,
          component: StoringPromptsContent
        },
        'composing-prompts': {
          id: 'composing-prompts',
          title: 'Composing and Copying Prompts',
          order: 2,
          component: ComposingPromptsContent
        },
        'code-maps': {
          id: 'code-maps',
          title: 'Using Code Maps for Token Efficiency',
          order: 3,
          component: CodeMapsContent
        }
      }
    },
    'api-integration': {
      id: 'api-integration',
      title: 'API Integration',
      order: 5,
      subsections: {
        'supported-models': {
          id: 'supported-models',
          title: 'Supported Models',
          order: 1,
          component: SupportedModelsContent
        },
        'integrated-chat': {
          id: 'integrated-chat',
          title: 'Using the Integrated Chat',
          order: 2,
          component: IntegratedChatContent
        },
        'pro-mode': {
          id: 'pro-mode',
          title: 'Pro Mode for Advanced Workflows',
          order: 3,
          component: ProModeContent
        }
      }
    },
    'applying-changes': {
      id: 'applying-changes',
      title: 'Applying Changes',
      order: 6,
      subsections: {
        'multi-file-edits': {
          id: 'multi-file-edits',
          title: 'Reviewing Multi-File Edits',
          order: 1,
          component: MultiFileEditsContent
        },
        'apply-mode': {
          id: 'apply-mode',
          title: 'Using Apply Mode for External AI',
          order: 2,
          component: ApplyModeContent
        }
      }
    },
    'faq': {
      id: 'faq',
      title: 'FAQ',
      order: 7,
      subsections: {
        'what-is-repo-prompt': {
          id: 'what-is-repo-prompt',
          title: 'What is RepoPrompt?',
          order: 1,
          component: WhatIsRepoPromptContent
        },
        'cursor-windsurf-difference': {
          id: 'cursor-windsurf-difference',
          title: 'Difference from Cursor/Windsurf',
          order: 2,
          component: CursorWindsurfDifferenceContent
        },
        'windows-linux': {
          id: 'windows-linux',
          title: 'Windows/Linux Availability',
          order: 3,
          component: WindowsLinuxContent
        },
        'api-keys': {
          id: 'api-keys',
          title: 'Using API Keys',
          order: 4,
          component: APIKeysContent
        },
        'pay-for-services': {
          id: 'pay-for-services',
          title: 'Paying for Services',
          order: 5,
          component: PayForServicesContent
        },
        'prompt-caching': {
          id: 'prompt-caching',
          title: 'Prompt Caching',
          order: 6,
          component: PromptCachingContent
        },
        'manage-subscription': {
          id: 'manage-subscription',
          title: 'Managing Your Subscription',
          order: 7,
          component: ManageSubscriptionContent
        },
        'solo-project': {
          id: 'solo-project',
          title: 'Is This a Solo Project?',
          order: 8,
          component: SoloProjectContent
        }
      }
    }
  },
  // Maintain order information separately for rendering
  sectionOrder: ['getting-started', 'file-selection', 'workspace-management', 'prompt-management', 'api-integration', 'applying-changes', 'faq']
};

// Helper function to get section overview content
export const getSectionOverview = (sectionId) => {
  switch (sectionId) {
    case 'getting-started':
      return "Learn how to install and get started with RepoPrompt, from setup to opening your first workspace.";
    case 'file-selection':
      return "Understand different methods for selecting, sorting, and filtering files in your workspace.";
    case 'workspace-management':
      return "Learn how to effectively manage workspaces, perform file operations, take advantage of automatic file watching, and integrate with other applications using URL schemes.";
    case 'prompt-management':
      return "Discover how to create, store, and reuse prompts efficiently, including token-saving Code Maps.";
    case 'api-integration':
      return "Connect RepoPrompt with various AI models through their APIs and use integrated chat features.";
    case 'applying-changes':
      return "Learn how to review and apply AI-suggested changes to your files, whether from integrated chat or external sources.";
    case 'faq':
      return "Find answers to common questions about RepoPrompt functionality and features.";
    default:
      return `Overview for the ${sectionId} section.`;
  }
};

export default documentationData;